import { useMemo } from 'react';
import { createPortal } from "react-dom";
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from "@peacecover/component";
import { usePreventTouchScroll } from "../../hooks/usePreventTouchScroll";

interface OriginalEpisodesLoadingOverlayProps {
  onClose: () => void;
}

const OriginalEpisodesLoadingOverlay = ({ onClose }: OriginalEpisodesLoadingOverlayProps) => {
  const { t } = useTranslation();
  usePreventTouchScroll();

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.6)] z-[9999]"
      style={{
        backdropFilter: 'blur(7.8px)',
        WebkitBackdropFilter: 'blur(7.8px)',
        touchAction: 'auto'
      }}
    >
      <div
        className="flex flex-col items-center h-[610px] w-full relative overflow-hidden"
        style={{
          backgroundImage: 'url(/assets/scene/bg_episode_loading_overlay.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute w-[173px] h-[69px] top-[270px] left-[50%] -translate-x-1/2 pointer-events-none z-0">
          <StarryNight count={20} startOpacity={0.8} />
        </div>

        <div className="absolute w-[301px] h-[146px] top-[234px] left-[50%] -translate-x-1/2 pointer-events-none z-0">
          <StarryNight count={25} startOpacity={0.4} />
        </div>

        <img
          src="/assets/scene/fountain_pen.png"
          alt="fountain pen"
          className="relative z-10 w-[167px] h-[167px] mt-[165px] ml-[10px]"
        />

        <div className="relative z-10 flex flex-col items-center gap-[12px] mt-[15px]">
          <p className="text-[20px] font-bold leading-[24px] text-pure-white text-center whitespace-pre-line">
            {t('OriginalEpisodesLoadingOverlay.title')}
          </p>
          <p className="text-[15px] font-medium leading-[18px] text-main-dark-2">
            {t('OriginalEpisodesLoadingOverlay.description')}
          </p>
        </div>
        <Button
          className="relative z-10 px-[18px] h-[40px] text-[14px] font-semibold text-pure-white bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)] mt-[58px]"
          onClick={onClose}
        >
          {t('OriginalEpisodesLoadingOverlay.button')}
        </Button>
      </div>
    </div>,
    document.body
  );
};

export default OriginalEpisodesLoadingOverlay;

interface StarConfig {
  id: string;
  top: number;
  left: number;
  size: number;
  duration: number;
  delay: number;
  opacityKeyframes: number[];
  scaleKeyframes: number[];
  initialPhaseIndex: number;
}

const createStarConfigs = (count: number, startOpacity: number): StarConfig[] =>
  Array.from({ length: count }, (_, i) => {
    const top = Math.random() * 100;
    const left = Math.random() * 100;
    const size = 1 + Math.random() * 2;
    const opacityKeyframes = [
      startOpacity,
      startOpacity * (0.35 + Math.random() * 0.2),
      startOpacity * (0.7 + Math.random() * 0.2),
      startOpacity * (0.25 + Math.random() * 0.15),
      startOpacity * (0.55 + Math.random() * 0.2),
      startOpacity * (0.25 + Math.random() * 0.15),
      startOpacity
    ];
    const scaleKeyframes = [1, 0.6, 0.9, 0.5, 0.85, 0.55, 1];
    return {
      id: `star-${top}-${left}-${size}-${i}`,
      top,
      left,
      size,
      duration: 1.5 + Math.random() * 1.5,
      delay: Math.random() * 2,
      opacityKeyframes,
      scaleKeyframes,
      initialPhaseIndex: Math.floor(Math.random() * opacityKeyframes.length)
    };
  });

const Star = ({ config }: { config: StarConfig }) => {
  const phaseIdx = config.initialPhaseIndex;
  const initialOpacity = config.opacityKeyframes[phaseIdx];
  const initialScale = config.scaleKeyframes[phaseIdx];

  return (
    <motion.div
      className="absolute rounded-full bg-white pointer-events-none"
      style={{
        width: `${config.size}px`,
        height: `${config.size}px`,
        top: `${config.top}%`,
        left: `${config.left}%`
      }}
      initial={{
        opacity: initialOpacity,
        scale: initialScale
      }}
      animate={{
        opacity: config.opacityKeyframes,
        scale: config.scaleKeyframes
      }}
      transition={{
        duration: config.duration,
        delay: config.delay,
        repeat: Number.POSITIVE_INFINITY,
        repeatType: 'reverse'
      }}
    />
  );
};

const StarryNight = ({
  count = 30,
  startOpacity = 0.8
}: {
  count: number;
  startOpacity?: number;
}) => {
  const configs = useMemo(() => createStarConfigs(count, startOpacity), [count, startOpacity]);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {configs.map((config) => (
        <Star key={config.id} config={config} />
      ))}
    </div>
  );
};
